import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import PropTypes from 'prop-types';

// material-ui
import ListItem from '@mui/material/ListItem';
import useMediaQuery from '@mui/material/useMediaQuery';
import Badge from '@mui/material/Badge';
import ClickAwayListener from '@mui/material/ClickAwayListener';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import ListItemText from '@mui/material/ListItemText';
import Paper from '@mui/material/Paper';
import Popper from '@mui/material/Popper';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';

// project imports
import MainCard from 'components/MainCard';
import IconButton from 'components/@extended/IconButton';
import Transitions from 'components/@extended/Transitions';

// assets
import BellOutlined from '@ant-design/icons/BellOutlined';
import CheckCircleOutlined from '@ant-design/icons/CheckCircleOutlined';
import ArrowRightOutlined from '@ant-design/icons/ArrowRightOutlined';

// hooks
import { useUnreadNotifications, useMarkAllAsRead, useUnreadNotificationCount } from 'hooks/useNotifications';

// Constants
const NOTIFICATION_LIMITS = {
  MAX_HEIGHT: 400,
  FADE_THRESHOLD: 6
};

const SCROLLBAR_STYLES = {
  width: '6px',
  track: {
    backgroundColor: 'rgba(0,0,0,0.1)',
    borderRadius: '3px'
  },
  thumb: {
    backgroundColor: 'rgba(0,0,0,0.3)',
    borderRadius: '3px',
    hover: 'rgba(0,0,0,0.5)'
  }
};

const FADE_GRADIENT = 'linear-gradient(transparent, rgba(255,255,255,0.8))';

// Helper function to format time ago
const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMinutes = Math.floor((now - date) / (1000 * 60));

  if (diffInMinutes < 1) return 'Just now';
  if (diffInMinutes < 60) return `${diffInMinutes}m ago`;

  const diffInHours = Math.floor(diffInMinutes / 60);
  if (diffInHours < 24) return `${diffInHours}h ago`;

  const diffInDays = Math.floor(diffInHours / 24);
  if (diffInDays < 7) return `${diffInDays}d ago`;

  return date.toLocaleDateString();
};

// Helper component for loading state
const LoadingContent = () => (
  <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
    <CircularProgress size={24} />
  </Box>
);

// Helper component for error state
const ErrorContent = ({ error }) => (
  <Box sx={{ textAlign: 'center', py: 2 }}>
    <Typography variant="body2" color="error">
      {error?.message || 'Failed to load notifications'}
    </Typography>
  </Box>
);

ErrorContent.propTypes = {
  error: PropTypes.shape({
    message: PropTypes.string
  })
};

// Helper component for empty state
const EmptyContent = () => (
  <Box sx={{ textAlign: 'center', py: 3 }}>
    <Typography variant="body2" color="text.secondary">
      No notifications
    </Typography>
  </Box>
);

// Helper component for notification item
const NotificationItem = ({ notification, onNotificationClick }) => (
  <ListItem
    key={notification._id}
    component={ListItemButton}
    divider
    selected={!notification.read}
    onClick={() => onNotificationClick(notification)}
    sx={{
      cursor: 'pointer',
      '&:hover': {
        backgroundColor: 'action.hover'
      }
    }}
    secondaryAction={
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <Typography variant="caption" noWrap>
          {formatTimeAgo(notification.createdAt)}
        </Typography>
        <ArrowRightOutlined
          style={{
            fontSize: '12px',
            color: 'rgba(0, 0, 0, 0.4)',
            opacity: 0.7
          }}
        />
      </Box>
    }
  >
    <Tooltip title="Click to view UGC submission" placement="left">
      <ListItemText
        primary={
          <Typography
            variant="h6"
            sx={{
              fontWeight: notification.read ? 'normal' : 'bold',
              color: notification.read ? 'text.secondary' : 'text.primary'
            }}
          >
            {notification.message}
          </Typography>
        }
        secondary={
          notification.ugc && (
            <Typography variant="caption" color="primary">
              UGC Submission • Click to view details
            </Typography>
          )
        }
      />
    </Tooltip>
  </ListItem>
);

NotificationItem.propTypes = {
  notification: PropTypes.shape({
    _id: PropTypes.string.isRequired,
    read: PropTypes.bool,
    createdAt: PropTypes.string.isRequired,
    message: PropTypes.string.isRequired,
    ugc: PropTypes.object
  }).isRequired,
  onNotificationClick: PropTypes.func.isRequired
};

// Helper component for scrollable notification list
const ScrollableNotificationList = ({ notifications, onNotificationClick, avatarSX, actionSX }) => {
  const shouldShowFade = notifications.length > NOTIFICATION_LIMITS.FADE_THRESHOLD;
  
  return (
    <Box
      sx={{
        maxHeight: NOTIFICATION_LIMITS.MAX_HEIGHT,
        overflowY: 'auto',
        overflowX: 'hidden',
        position: 'relative',
        '&::-webkit-scrollbar': {
          width: SCROLLBAR_STYLES.width
        },
        '&::-webkit-scrollbar-track': SCROLLBAR_STYLES.track,
        '&::-webkit-scrollbar-thumb': {
          ...SCROLLBAR_STYLES.thumb,
          '&:hover': {
            backgroundColor: SCROLLBAR_STYLES.thumb.hover
          }
        },
        scrollbarWidth: 'thin',
        scrollbarColor: `${SCROLLBAR_STYLES.thumb.backgroundColor} ${SCROLLBAR_STYLES.track.backgroundColor}`,
        '&::after': shouldShowFade
          ? {
              content: '""',
              position: 'absolute',
              bottom: 0,
              left: 0,
              right: 0,
              height: '20px',
              background: FADE_GRADIENT,
              pointerEvents: 'none',
              zIndex: 1
            }
          : {}
      }}
    >
      <List
        component="nav"
        sx={{
          p: 0,
          '& .MuiListItemButton-root': {
            py: 0.5,
            px: 2,
            '&.Mui-selected': { bgcolor: 'grey.50', color: 'text.primary' },
            '& .MuiAvatar-root': avatarSX,
            '& .MuiListItemSecondaryAction-root': { ...actionSX, position: 'relative' }
          }
        }}
      >
        {notifications.map((notification) => (
          <NotificationItem
            key={notification._id}
            notification={notification}
            onNotificationClick={onNotificationClick}
          />
        ))}
      </List>
    </Box>
  );
};

ScrollableNotificationList.propTypes = {
  notifications: PropTypes.arrayOf(
    PropTypes.shape({
      _id: PropTypes.string.isRequired,
      read: PropTypes.bool,
      createdAt: PropTypes.string.isRequired,
      message: PropTypes.string.isRequired,
      ugc: PropTypes.object
    })
  ).isRequired,
  onNotificationClick: PropTypes.func.isRequired,
  avatarSX: PropTypes.object.isRequired,
  actionSX: PropTypes.object.isRequired
};

// sx styles
const avatarSX = {
  width: 36,
  height: 36,
  fontSize: '1rem'
};

const actionSX = {
  mt: '6px',
  ml: 1,
  top: 'auto',
  right: 'auto',
  alignSelf: 'flex-start',

  transform: 'none'
};

// ==============================|| HEADER CONTENT - NOTIFICATION ||============================== //

export default function Notification() {
  const downMD = useMediaQuery((theme) => theme.breakpoints.down('md'));
  const navigate = useNavigate();

  const anchorRef = useRef(null);
  const [open, setOpen] = useState(false);

  // React Query hooks
  const {
    data: notifications = [],
    isLoading: loading,
    error,
    refetch: refetchNotifications
  } = useUnreadNotifications({
    enabled: open // Only fetch when dropdown is open
  });

  // Hook to get unread notification count
  const { data: unreadCount = 0, refetch: refetchCount } = useUnreadNotificationCount();

  const markAllAsReadMutation = useMarkAllAsRead();

  const handleToggle = () => {
    setOpen((prevOpen) => {
      const newOpen = !prevOpen;

      // If opening the dropdown, fetch fresh notifications
      if (newOpen) {
        refetchNotifications();
        refetchCount();
      }

      return newOpen;
    });
  };

  const handleClose = (event) => {
    if (anchorRef.current?.contains(event.target)) {
      return;
    }
    setOpen(false);
  };

  const handleMarkAllAsRead = () => {
    markAllAsReadMutation.mutate();
  };

  const handleNotificationClick = (notification) => {
    // Close the dropdown
    setOpen(false);

    // Navigate to UGC submission page
    navigate('/ugc-submissions');
  };



  return (
    <Box sx={{ flexShrink: 0, ml: 0.75 }}>
      <IconButton
        color="secondary"
        variant="light"
        sx={(theme) => ({
          color: 'text.primary',
          bgcolor: open ? 'grey.100' : 'transparent',
          ...theme.applyStyles('dark', { bgcolor: open ? 'background.default' : 'transparent' })
        })}
        aria-label="open profile"
        ref={anchorRef}
        aria-controls={open ? 'profile-grow' : undefined}
        aria-haspopup="true"
        onClick={handleToggle}
      >
        <Badge badgeContent={unreadCount} color="primary">
          <BellOutlined />
        </Badge>
      </IconButton>
      <Popper
        placement={downMD ? 'bottom' : 'bottom-end'}
        open={open}
        anchorEl={anchorRef.current}
        role={undefined}
        transition
        disablePortal
        popperOptions={{ modifiers: [{ name: 'offset', options: { offset: [downMD ? -5 : 0, 9] } }] }}
      >
        {({ TransitionProps }) => (
          <Transitions type="grow" position={downMD ? 'top' : 'top-right'} in={open} {...TransitionProps}>
            <Paper sx={(theme) => ({ boxShadow: theme.customShadows.z1, width: '100%', minWidth: 285, maxWidth: { xs: 285, md: 420 } })}>
              <ClickAwayListener onClickAway={handleClose}>
                <MainCard
                  title={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <Typography variant="h5">Notifications</Typography>
                      {notifications.length > 0 && (
                        <Typography variant="caption" color="text.secondary">
                          ({notifications.length})
                        </Typography>
                      )}
                    </Box>
                  }
                  elevation={0}
                  border={false}
                  content={false}
                  secondary={
                    <>
                      {unreadCount > 0 && (
                        <Tooltip title="Mark all as read">
                          <IconButton color="success" size="small" onClick={handleMarkAllAsRead} disabled={markAllAsReadMutation.isPending}>
                            {markAllAsReadMutation.isPending ? (
                              <CircularProgress size={16} />
                            ) : (
                              <CheckCircleOutlined style={{ fontSize: '1.15rem' }} />
                            )}
                          </IconButton>
                        </Tooltip>
                      )}
                    </>
                  }
                >
                  {(() => {
                    if (loading) {
                      return <LoadingContent />;
                    }
                    
                    if (error) {
                      return <ErrorContent error={error} />;
                    }
                    
                    if (notifications.length === 0) {
                      return <EmptyContent />;
                    }
                    
                    return (
                      <ScrollableNotificationList
                        notifications={notifications}
                        onNotificationClick={handleNotificationClick}
                        avatarSX={avatarSX}
                        actionSX={actionSX}
                      />
                    );
                  })()}
                </MainCard>
              </ClickAwayListener>
            </Paper>
          </Transitions>
        )}
      </Popper>
    </Box>
  );
}
